import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Copy, RefreshCw, Eye, EyeOff, Key } from 'lucide-react';

interface StreamKeyManagerProps {
  streamId?: string;
}

export const StreamKeyManager = ({ streamId }: StreamKeyManagerProps) => {
  const [streamKey, setStreamKey] = useState<string>('');
  const [rtmpUrl, setRtmpUrl] = useState<string>('');
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (streamId) {
      loadStreamKey();
    } else {
      generateNewKey();
    }
  }, [streamId]);

  const generateNewKey = (): string => {
    // Use crypto API for secure random generation
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    const key = `live_${Array.from(array, byte => 
      'abcdefghijklmnopqrstuvwxyz0123456789'[byte % 36]
    ).join('')}`;
    setStreamKey(key);
    setRtmpUrl(`rtmp://stream.yourplatform.live/live`);
    return key;
  };

  const loadStreamKey = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('stream_credentials')
        .select('stream_key')
        .eq('stream_id', streamId)
        .maybeSingle();

      if (error) throw error;

      if (data?.stream_key) {
        setStreamKey(data.stream_key);
        setRtmpUrl(`rtmp://stream.yourplatform.live/live`);
      } else {
        // Auto-generate and save key for new streams
        const newKey = generateNewKey();
        await saveStreamKeyToDb(newKey);
      }
    } catch (error) {
      console.error('Error loading stream key:', error);
      toast.error('Failed to load stream key');
      generateNewKey(); // Fallback to local key
    } finally {
      setLoading(false);
    }
  };

  const saveStreamKeyToDb = async (key: string) => {
    if (!streamId) return;
    
    try {
      const { error } = await supabase
        .from('stream_credentials')
        .upsert({
          stream_id: streamId,
          stream_key: key,
        }, {
          onConflict: 'stream_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error auto-saving stream key:', error);
    }
  };

  const saveStreamKey = async () => {
    if (!streamId) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('stream_credentials')
        .upsert({
          stream_id: streamId,
          stream_key: streamKey,
        });

      if (error) throw error;
      toast.success('Stream key saved');
    } catch (error) {
      console.error('Error saving stream key:', error);
      toast.error('Failed to save stream key');
    } finally {
      setLoading(false);
    }
  };

  const regenerateKey = () => {
    generateNewKey();
    toast.info('New stream key generated. Remember to save!');
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Stream Configuration
        </CardTitle>
        <CardDescription>
          Use these credentials with your streaming software (OBS, Streamlabs, etc.)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>RTMP URL</Label>
          <div className="flex gap-2">
            <Input value={rtmpUrl} readOnly className="font-mono text-sm" />
            <Button
              variant="outline"
              size="icon"
              onClick={() => copyToClipboard(rtmpUrl, 'RTMP URL')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Stream Key</Label>
          <div className="flex gap-2">
            <Input
              type={showKey ? 'text' : 'password'}
              value={streamKey}
              readOnly
              className="font-mono text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowKey(!showKey)}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => copyToClipboard(streamKey, 'Stream key')}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Keep your stream key private. Anyone with this key can stream to your channel.
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={regenerateKey}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Regenerate Key
          </Button>
          {streamId && (
            <Button onClick={saveStreamKey} disabled={loading}>
              {loading ? 'Saving...' : 'Save Configuration'}
            </Button>
          )}
        </div>

        <div className="bg-muted p-4 rounded-lg space-y-2">
          <h4 className="font-semibold text-sm">Quick Setup Guide</h4>
          <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
            <li>Copy the RTMP URL and Stream Key above</li>
            <li>Open your streaming software (OBS, Streamlabs, etc.)</li>
            <li>Go to Settings → Stream</li>
            <li>Paste the RTMP URL as "Server"</li>
            <li>Paste the Stream Key as "Stream Key"</li>
            <li>Click "START STREAM" to go live</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};
